import React, { useState } from "react";
import { Button, Checkbox, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { AvailableFilter } from "../../index";
import styles from "./FilterItem.module.scss";
import classNames from "classnames";
import ChevronIcon from "assets/icons/ChevronIcon";
import { VLDropdown } from "../../../VLDropdown/VLDropdown";

interface FilterItemProps {
  filter: AvailableFilter;
  selectedValues: string[];
  onFilterChange: (filterKey: string, values: string[] | null) => void;
}

/** Order-independent comparison of committed vs in-dropdown selections */
function areSelectionsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

function FilterItem({
  filter,
  selectedValues,
  onFilterChange,
}: FilterItemProps) {
  const [searchText, setSearchText] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempValues, setTempValues] = useState<string[]>([]);

  const handleDropdownOpenChange = (open: boolean) => {
    setDropdownOpen(open);

    if (open) {
      setTempValues(selectedValues);
    }
  };

  const handleOptionChange = (value: string, checked: boolean) => {
    setTempValues((prev) => {
      if (filter.type === "single") {
        return checked ? [value] : [];
      } else {
        if (checked) {
          return prev.includes(value) ? prev : [...prev, value];
        } else {
          return prev.filter((v) => v !== value);
        }
      }
    });
  };

  const handleOK = () => {
    onFilterChange(filter.key, tempValues.length > 0 ? tempValues : null);
    setDropdownOpen(false);
  };

  const handleReset = () => {
    setTempValues([]);
    setSearchText("");
  };

  const getFilteredOptions = () => {
    return filter.options.filter((opt) =>
      opt.text.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  const getFilterText = () => {
    if (selectedValues.length === 0) {
      return { title: filter.title, selection: "", additionalCount: 0 };
    }
    if (selectedValues.length === 1) {
      const option = filter.options.find(
        (opt) => opt.value === selectedValues[0],
      );
      return {
        title: filter.title,
        selection: option?.text || selectedValues[0],
        additionalCount: 0,
      };
    }
    const firstOption = filter.options.find(
      (opt) => opt.value === selectedValues[0],
    );
    return {
      title: filter.title,
      selection: firstOption?.text || selectedValues[0],
      additionalCount: selectedValues.length - 1,
    };
  };

  const renderFilterDropdown = () => {
    const filteredOptions = getFilteredOptions();
    const resetDisabled = tempValues.length === 0;
    const okDisabled = areSelectionsEqual(tempValues, selectedValues);

    return (
      <div
        className={classNames(
          "ant-table-filter-dropdown",
          styles.filterDropdown,
        )}
      >
        {filter.searchable && (
          <div className="ant-table-filter-dropdown-search">
            <Input
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              size="small"
            />
          </div>
        )}

        <div className="ant-table-filter-dropdown-content">
          <div className={styles.dropdownContent}>
            {filteredOptions.map((option) => {
              const isChecked = tempValues.includes(option.value);

              const handleClick = () => {
                handleOptionChange(option.value, !isChecked);
              };

              return (
                <div
                  key={option.value}
                  className={styles.optionItem}
                  onClick={handleClick}
                >
                  <Checkbox checked={isChecked} className={styles.checkbox}>
                    {option.text}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.buttonsWrapper}>
          <Button
            type="link"
            size="small"
            disabled={resetDisabled}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={okDisabled}
            onClick={handleOK}
          >
            OK
          </Button>
        </div>
      </div>
    );
  };

  const filterText = getFilterText();
  const isActive = selectedValues.length > 0;

  return (
    <VLDropdown
      popupRender={renderFilterDropdown}
      trigger={["click"]}
      placement="bottomLeft"
      open={dropdownOpen}
      onOpenChange={handleDropdownOpenChange}
    >
      <div
        className={classNames(styles.filterTrigger, {
          [styles.active]: isActive,
        })}
      >
        <div className={styles.titleContainer}>
          <div className={styles.titleTextContainer}>
            <div className={styles.filterTitle}>
              {filterText.title}
              {filterText.selection && ":"}
            </div>
            {filterText.selection && (
              <div className={styles.filterSelection}>
                {filterText.selection}
              </div>
            )}
            {filterText.additionalCount > 0 && (
              <div className={styles.additionalCount}>
                +{filterText.additionalCount}
              </div>
            )}
          </div>
          <div className={styles.filterIcon}>
            <ChevronIcon direction={dropdownOpen ? "up" : "down"} />
          </div>
        </div>
      </div>
    </VLDropdown>
  );
}

export default FilterItem;
